import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AirflowTaskInstance } from '../../types';

type ClearTaskOptions = {
    includePast: boolean;
    includeFuture: boolean;
    includeUpstream: boolean;
    includeDownstream: boolean;
    onlyFailed: boolean;
    preventRerun: boolean;
};

const DEFAULT_OPTIONS: ClearTaskOptions = {
    includePast: false,
    includeFuture: false,
    includeUpstream: false,
    includeDownstream: true,
    onlyFailed: false,
    preventRerun: true,
};

/*
 * Manages the Clear Task dialog's toggle options and live dry-run preview.
 * Note: `preventRerun` is tracked in state but intentionally not sent to the API — this
 * mirrors the source implementation's behavior exactly rather than silently changing it.
 */
export function useClearTaskDialog(dagId: string, task: AirflowTaskInstance, onClose: () => void, onCleared: () => void) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [options, setOptions] = useState<ClearTaskOptions>(DEFAULT_OPTIONS);
    const [affectedTasks, setAffectedTasks] = useState<AirflowTaskInstance[]>([]);
    const [isLoadingPreview, setIsLoadingPreview] = useState(true);
    const [isClearing, setIsClearing] = useState(false);

    /*
     * Toggles a single clear option
     */
    const handleOptionChange = useCallback((key: keyof ClearTaskOptions, value: boolean) => {
        setOptions((prev) => ({ ...prev, [key]: value }));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setIsLoadingPreview(true);
        AirflowDashboardHandler.clearTaskInstances(Env, dagId, {
            dryRun: true,
            dagRunId: task.dag_run_id,
            taskIds: [task.task_id],
            includePast: options.includePast,
            includeFuture: options.includeFuture,
            includeUpstream: options.includeUpstream,
            includeDownstream: options.includeDownstream,
            onlyFailed: options.onlyFailed,
        })
            .then((response) => {
                if (!cancelled) setAffectedTasks(response.task_instances);
            })
            .catch(() => {
                if (!cancelled) errorAlert('Failed to preview affected tasks.');
            })
            .finally(() => {
                if (!cancelled) setIsLoadingPreview(false);
            });
        return () => {
            cancelled = true;
        };
    }, [Env, dagId, task.dag_run_id, task.task_id, options.includePast, options.includeFuture, options.includeUpstream, options.includeDownstream, options.onlyFailed]);

    /*
     * Executes the clear for real, then notifies the caller to refresh
     */
    const handleConfirm = useCallback(async () => {
        setIsClearing(true);
        try {
            await AirflowDashboardHandler.clearTaskInstances(Env, dagId, {
                dryRun: false,
                dagRunId: task.dag_run_id,
                taskIds: [task.task_id],
                includePast: options.includePast,
                includeFuture: options.includeFuture,
                includeUpstream: options.includeUpstream,
                includeDownstream: options.includeDownstream,
                onlyFailed: options.onlyFailed,
            });
            successAlert(`Cleared task "${task.task_id}".`);
            onCleared();
            onClose();
        } catch {
            errorAlert(`Failed to clear task "${task.task_id}".`);
        } finally {
            setIsClearing(false);
        }
    }, [Env, dagId, task, options, onCleared, onClose]);

    return { options, handleOptionChange, affectedTasks, isLoadingPreview, isClearing, handleConfirm };
}
